import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  WritableSignal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  ElderDataToolbarComponent,
  ElderNumberCellDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  LocalListDataSource,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { map, Observable } from 'rxjs'
import { Material } from '../../../../../materials/material'
import { MaterialService } from '../../../../../materials/material.service'
import {
  emptyMaterialSpec,
  PackagingMaterialSpec,
  PopulatedPackagingMaterialSpec,
} from '../../../package-type'
import { PackageTypeDetailController } from '../../package-type-detail-controller.service'
import {
  PackageTypeMaterialDetailDialogComponent,
  PackageTypeMaterialDialogData,
} from '../package-type-material-detail-dialog/package-type-material-detail-dialog.component'

@Component({
  selector: 'app-package-type-material-table',
  imports: [
    ElderNumberCellDirective,
    ElderTableComponent,
    ElderDataToolbarComponent,
    ElderTableToolbarDirective,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatSort,
    MatSortHeader,
    ElderTranslatePipe,
    FormsModule,
    MatHeaderCellDef,
  ],
  templateUrl: './package-type-material-table.component.html',
})
export class PackageTypeMaterialTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly materialService = inject(MaterialService)
  private readonly dialog = inject(MatDialog)
  private readonly destroyRef = inject(DestroyRef)
  private readonly controller = inject(PackageTypeDetailController)

  /**
   * Wrapped in datasource to avoid array change detection issues
   */
  public readonly packagingMaterialSpecs =
    input.required<LocalListDataSource<PackagingMaterialSpec>>()

  readonly populatedSpecs: WritableSignal<PopulatedPackagingMaterialSpec[]> = signal([])

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    effect(() => {
      if (this.packagingMaterialSpecs().data.length > 0) {
        this.buildMaterialSpecs(this.packagingMaterialSpecs().data)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((populatedSpecs) => this.populatedSpecs.set(populatedSpecs))
      }
      this.packagingMaterialSpecs().dataChanged.subscribe((_) => {
        const pt = this.controller.packageType
        pt.supportedPackagingMaterials = this.packagingMaterialSpecs().data
        this.controller.packageType = pt
        this.buildMaterialSpecs(this.packagingMaterialSpecs().data)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((populatedSpecs) => this.populatedSpecs.set(populatedSpecs))
      })
    })
  }

  /***************************************************************************
   *                                                                         *
   * Template methods                                                        *
   *                                                                         *
   **************************************************************************/

  create(): void {
    const empty = emptyMaterialSpec()
    this.openDialog(empty)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((spec: PackagingMaterialSpec) => {
        if (spec) {
          this.packagingMaterialSpecs().save(spec)
        }
      })
  }

  openDetail(populated: PopulatedPackagingMaterialSpec): void {
    const existing = this.packagingMaterialSpecs().data.find(
      (spec) => spec.materialId === populated.materialId
    )!
    this.openDialog(existing)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((spec: PackagingMaterialSpec) => {
        if (spec) {
          this.packagingMaterialSpecs().save(spec)
        }
      })
  }

  delete(toDelete?: PopulatedPackagingMaterialSpec[]): void {
    if (toDelete && toDelete.length > 0) {
      const ids = toDelete.map((mat) => mat.materialId)
      this.packagingMaterialSpecs().deleteAllById(ids)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildMaterialSpecs(
    specs: PackagingMaterialSpec[]
  ): Observable<PopulatedPackagingMaterialSpec[]> {
    const materialIds = specs.map((s) => s.materialId)
    return this.loadMaterials(materialIds).pipe(
      map((materials) => this.populateSpecs(specs, materials))
    )
  }

  private loadMaterials(ids: string[]): Observable<Material[]> {
    return this.materialService.findByIds(ids)
  }

  private populateSpecs(
    specs: PackagingMaterialSpec[],
    materials: Material[]
  ): PopulatedPackagingMaterialSpec[] {
    return materials.map((material) => {
      const spec = specs.find((s) => s.materialId === material.id)
      return new PopulatedPackagingMaterialSpec(spec.materialId, spec.presetQuantity, material)
    })
  }

  private openDialog(
    materialSpec: PackagingMaterialSpec
  ): MatDialogRef<PackageTypeMaterialDetailDialogComponent> {
    return this.dialog.open(PackageTypeMaterialDetailDialogComponent, {
      minWidth: '35vw',
      minHeight: '35vh',
      data: <PackageTypeMaterialDialogData>{
        materialSpec,
      },
    })
  }
}
